// ─────────────────────────────────────────────
//  onCallChipLabel — what the standing-watch chip on a free provider says.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
//
//  ⚠️ IT USED TO SAY "OB CALL" ALWAYS, AND THAT WAS A LIE ON EVERY OTHER ROOM.
//  Three surfaces printed the literal string for anyone `isStandingOnCall`
//  matched, and the watch is not an OB-only arrangement: Wesley Chapel,
//  2026-08-20, ran two standing watches on the same day — Maria McKay in WC OB
//  and Brittney McDowell in WC IR — and the bench badged BOTH of them "OB CALL".
//  The owner read it exactly as written: *"why 2 ob on call: only one maria
//  mckay."* A chip that names the wrong service is worse than one that names
//  none, because the reader acts on it: whoever needs an epidural covered walks
//  to the wrong person.
//
//  ⚠️ AND IT DEGRADES TO "ON CALL" RATHER THAN GUESSING. A room label the rule
//  cannot read as a service (`OR 12`, `WC E1`) yields the plain form — true, and
//  the room is still named in the chip's tooltip. Inventing a service from a
//  room number is the failure this function exists to end.

// The service token is the LAST word of the room label, and only when it is two
// or three letters with no digits: `WC OB` → OB, `WC IR` → IR, `WC MRI` → MRI.
// `WC E1` and `OR 12` carry a digit, so they are rooms, not services.
export function onCallService(roomName) {
  const room = String(roomName || "").trim();
  if (!room) return null;
  const last = room.split(/\s+/).pop().toUpperCase();
  return /^[A-Z]{2,3}$/.test(last) ? last : null;
}

// ⚠️ ONLY OB CARRIES A STANDING CALL — owner, 2026-08-20, on being shown the
// first correct non-OB chip: *"no ir call, removed label."* The department runs
// one standing watch and it is obstetrics; a caseless 07:00→07:00 row in any
// other room is the sheet saying something else, and badging it invents a duty
// that does not exist. Returning null means the surfaces render NOTHING, which
// is the honest answer — not a vaguer chip.
//
// ⚠️ THIS HIDES A LABEL, IT DOES NOT CHANGE THE COUNT. Whoever holds that row is
// still on-call as far as `isStandingOnCall` and the staffing pool are
// concerned. If a non-OB caseless row should not count as cover either, that is
// a separate decision about the DATA, and it belongs at the import, not here.
export function onCallChipLabel(roomName) {
  return onCallService(roomName) === "OB" ? "OB CALL" : null;
}

// The tooltip always names the room in full, whichever label was chosen — the
// chip is short because it sits inside a name row, not because the detail is
// unimportant.
export function onCallChipTitle(roomName) {
  const room = String(roomName || "").trim();
  return room ? `On call for ${room} — free between cases` : "On call — free between cases";
}
